import { useMemo, useCallback } from 'react';
import { TIME_CONSTANTS, UI_CONSTANTS } from '../lib/constants';
import { formatDateToString } from '../lib/dateUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Doctor, Staff, Room, ScheduleItem } from './SchedulerTypes';

interface ManpowerScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDate: Date;
  scheduleItems: ScheduleItem[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  currentClinicName?: string;
}

interface StaffSchedule {
  staffId: string;
  name: string;
  priority: number;
  location: string;
  am1: string;
  am2: string;
  overrun: string;
  lunch: string;
  pm1: string;
  pm2: string;
  ot: string;
  roomAM: string;
  roomPM: string;
  isOnLeaveAll: boolean;
  // Add type tracking for each slot
  am1Type?: string;
  am2Type?: string;
  overrunType?: string;
  pm1Type?: string;
  pm2Type?: string;
  otType?: string;
}

const isSaturday = (date: Date): boolean => {
  return date.getDay() === 6;
};

// Normalize HH:mm / HH:mm:ss strings so boundary checks behave consistently.
const timeToMinutes = (time: string): number => {
  const [hours = '0', minutes = '0'] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
};

const isTimeBefore = (firstTime: string, secondTime: string): boolean => {
  return timeToMinutes(firstTime) < timeToMinutes(secondTime);
};

const isTimeAfter = (firstTime: string, secondTime: string): boolean => {
  return timeToMinutes(firstTime) > timeToMinutes(secondTime);
};

const isTimeOnOrBefore = (firstTime: string, secondTime: string): boolean => {
  return timeToMinutes(firstTime) <= timeToMinutes(secondTime);
};

const isTimeOnOrAfter = (firstTime: string, secondTime: string): boolean => {
  return timeToMinutes(firstTime) >= timeToMinutes(secondTime);
};

const timeOverlaps = (itemStart: string, itemEnd: string, rangeStart: string, rangeEnd: string): boolean => {
  return isTimeBefore(itemStart, rangeEnd) && isTimeAfter(itemEnd, rangeStart);
};

const formatTime = (time: string): string => {
  return time.replace(/:\d{2}$/, '');
};

const formatTimeWithPeriod = (time: string): string => {
  if (!time || typeof time !== 'string') {
    return '';
  }

  let cleanTime = time.replace(/:\d{2}$/, '');

  if (!cleanTime.includes(':')) {
    const hourOnly = parseInt(cleanTime, 10);
    if (!isNaN(hourOnly)) {
      cleanTime = `${hourOnly}:00`;
    }
  }

  const timeParts = cleanTime.split(':');
  if (timeParts.length !== 2) {
    return cleanTime;
  }

  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) {
    return cleanTime;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
};

const hasValidDuration = (slot: {start: string; end: string}): boolean => {
  return timeToMinutes(slot.start) < timeToMinutes(slot.end);
};

const NON_OCCUPANCY_TITLE_PATTERNS = [
  /NOT\s+ON\s+DUTY/i,
  /\bNOT\s+IN\b/i,
  /^\s*\d{1,2}(?::\d{2})?\s*(AM|PM)?\s*TO\s*\d{1,2}(?::\d{2})?\s*(AM|PM)?\s*ONLY\s*$/i,
];

// Doctor-unavailable placeholders should not block room availability, even if
// the current UI is still holding older cached schedule rows without sync metadata.
const isNonOccupancyRoomBlock = (item: ScheduleItem): boolean => {
  if (item.type !== 'appointment' || item.staffId || item.procedure) {
    return false;
  }

  const title = item.title?.trim() ?? '';
  const notes = item.notes?.toLowerCase() ?? '';

  if (notes.includes('patient:')) {
    return false;
  }

  return NON_OCCUPANCY_TITLE_PATTERNS.some(pattern => pattern.test(title));
};

const ManpowerScheduleModal = ({
  isOpen,
  onClose,
  currentDate,
  scheduleItems,
  doctors,
  staff,
  rooms,
  currentClinicName,
}: ManpowerScheduleModalProps) => {
  // Format current date to match schedule items format (YYYY-MM-DD) using Singapore timezone
  const formatDate = (date: Date): string => {
    return formatDateToString(date);
  };

  // Helper function to get detailed room availability for a specific time period
  const getRoomAvailabilityDetails = useCallback((timeSlot: 'morning' | 'afternoon' | 'full-day') => {
    const todayStr = formatDate(currentDate);
    
    // Define time period boundaries
    const periodStart = timeSlot === 'afternoon' ? '13:00' : '09:00';
    const periodEnd = timeSlot === 'morning' ? '12:00' : '18:00';
    
    // Process each room
    const roomAvailability = rooms
      .filter(room => room.status === 'active')
      .map(room => {
        // Get all schedules for this room today
        const roomSchedules = scheduleItems
          .filter(item => 
            item.date === todayStr && 
            item.roomId === room.id &&
            !isNonOccupancyRoomBlock(item)
          )
          .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
        
        // Calculate available slots
        const availableSlots: Array<{start: string; end: string}> = [];
        
        if (roomSchedules.length === 0) {
          // Room is completely free for the period
          availableSlots.push({ start: periodStart, end: periodEnd });
        } else {
          // Check for gap before first schedule
          const firstSchedule = roomSchedules[0];
          if (isTimeAfter(firstSchedule.startTime, periodStart)) {
            availableSlots.push({ 
              start: periodStart, 
              end: firstSchedule.startTime 
            });
          }
          
          // Check for gaps between schedules
          for (let i = 0; i < roomSchedules.length - 1; i++) {
            const currentSchedule = roomSchedules[i];
            const nextSchedule = roomSchedules[i + 1];
            
            if (isTimeBefore(currentSchedule.endTime, nextSchedule.startTime)) {
              // There's a gap between schedules
              const gapStart = currentSchedule.endTime;
              const gapEnd = nextSchedule.startTime;
              
              // Only include gaps within the period
              if (isTimeAfter(gapEnd, periodStart) && isTimeBefore(gapStart, periodEnd)) {
                availableSlots.push({
                  start: isTimeBefore(gapStart, periodStart) ? periodStart : gapStart,
                  end: isTimeAfter(gapEnd, periodEnd) ? periodEnd : gapEnd
                });
              }
            }
          }
          
          // Check for gap after last schedule
          const lastSchedule = roomSchedules[roomSchedules.length - 1];
          if (isTimeBefore(lastSchedule.endTime, periodEnd)) {
            availableSlots.push({ 
              start: lastSchedule.endTime, 
              end: periodEnd 
            });
          }
        }
        
        // Filter slots to only include those within the specified period
        // and trim them to the period boundaries
        const filteredSlots = availableSlots
          .filter(slot => isTimeAfter(slot.end, periodStart) && isTimeBefore(slot.start, periodEnd))
          .map(slot => ({
            start: isTimeBefore(slot.start, periodStart) ? periodStart : slot.start,
            end: isTimeAfter(slot.end, periodEnd) ? periodEnd : slot.end
          }))
          .filter(hasValidDuration); // Remove zero-duration slots
        
        // Format the availability string
        let availabilityString = room.name;
        if (filteredSlots.length === 0) {
          availabilityString += ' (Fully booked)';
        } else if (
          filteredSlots.length === 1 && 
          timeToMinutes(filteredSlots[0].start) === timeToMinutes(periodStart) && 
          timeToMinutes(filteredSlots[0].end) === timeToMinutes(periodEnd)
        ) {
          availabilityString += timeSlot === 'morning' 
            ? ' (Full morning available)' 
            : timeSlot === 'afternoon'
            ? ' (Full afternoon available)'
            : ' (Full day available)';
        } else {
          const slots = filteredSlots
            .filter(hasValidDuration) // Additional safety check: ensure we never display zero-duration slots
            .map(slot => `${formatTimeWithPeriod(slot.start)}-${formatTimeWithPeriod(slot.end)}`)
            .join(', ');
          availabilityString += ` (${slots})`;
        }
        
        return {
          room,
          availabilityString,
          hasAvailability: filteredSlots.length > 0
        };
      });
    
    return roomAvailability;
  }, [scheduleItems, currentDate, rooms]);

  // Helper function to determine cell color based on content and timing
  const getCellColorClass = (value: string, timeSlot: string, hasMCInRow: boolean = false, hasLeaveInRow: boolean = false, isSaturdayPM: boolean = false): string => {
    const baseClass = "h-8 text-xs py-1";
    
    // Saturday PM slots are disabled/greyed out
    if (isSaturdayPM) {
      return `${baseClass} bg-gray-200 text-gray-400`;
    }
    
    // If the row has MC type, use dark red for the whole row
    if (hasMCInRow) {
      return `${baseClass} bg-red-700 text-white`;
    }
    
    // If the row has leave type (but no MC), use red for the whole row
    if (hasLeaveInRow) {
      return `${baseClass} bg-red-500 text-white`;
    }
    
    // Training detection - Dark Blue
    if (value && value.toLowerCase().includes('training')) {
      return `${baseClass} bg-blue-800 text-white`;
    }
    
    // Reception detection - Purple
    if (value && (value.toLowerCase().includes('reception') || value.toLowerCase().includes('recep'))) {
      return `${baseClass} bg-purple-200 text-purple-800`;
    }
    
    // Dental Assisting detection
    if (value && value.toLowerCase().startsWith('dr ')) {
      // Multiple doctors - Darker Yellow to indicate shared assignment
      if (value.includes(',')) {
        return `${baseClass} bg-yellow-200 text-yellow-900`;
      }
      // Single doctor - Light Yellow
      return `${baseClass} bg-yellow-100 text-yellow-800`;
    }
    
    // OT detection - Bright Yellow (18:00+ timing)
    if (timeSlot === 'ot' && value) {
      return `${baseClass} bg-yellow-300 text-yellow-900`;
    }
    
    // Individual cell leave/MC detection (when not full day leave)
    if (value === 'URGENT LEAVE' || value === 'MC') {
      // MC/Urgent Leave gets red color
      return `${baseClass} bg-red-100 text-red-800`;
    }
    
    // Check if it's a regular leave (for individual cells)
    if (value && (value.includes('LEAVE') || value.includes('Leave')) && !value.includes('URGENT')) {
      return `${baseClass} bg-gray-400 text-gray-800`;
    }
    
    // Overrun detection - Light Blue (12:00-13:00 timing)
    if (timeSlot === 'overrun' && value) {
      return `${baseClass} bg-blue-100 text-blue-800`;
    }
    
    // Default styling for other content
    if (value) {
      return `${baseClass} bg-gray-50 text-gray-700`;
    }
    
    return baseClass;
  };

  // Helper function to get room assignment for a time period
  const getRoomForTimePeriod = useCallback((staffId: string, startTime: string, endTime: string): string => {
    // Filter today's schedule items first
    const todaySchedules = scheduleItems.filter(item => item.date === formatDate(currentDate));
    
    // Find schedules for this staff member that overlap with the time period
    const overlappingSchedules = todaySchedules.filter(item => {
      return item.staffId === staffId && 
             item.roomId &&
             timeOverlaps(item.startTime, item.endTime, startTime, endTime);
    });

    if (overlappingSchedules.length === 0) return '';

    // Get the room name for the first overlapping schedule
    const schedule = overlappingSchedules[0];
    const room = rooms?.find(r => r.id === schedule.roomId);
    return room ? room.name : '';
  }, [scheduleItems, currentDate, rooms]);

  // Helper function to get lunch time for a staff member
  const getLunchTime = useCallback((staffId: string, isOnLeaveAll: boolean): string => {
    // If staff is on leave all day, don't show lunch time
    if (isOnLeaveAll) {
      return '';
    }

    // No lunch break on Saturday
    if (isSaturday(currentDate)) {
      return '';
    }

    // Filter today's schedule items first
    const todaySchedules = scheduleItems.filter(item => item.date === formatDate(currentDate));
    
    // Find lunch schedule for this staff member
    // Look for schedule items with type 'break' and title containing 'lunch'
    const lunchSchedule = todaySchedules.find(item => {
      return item.staffId === staffId && 
             item.type === 'break' &&
             item.title && 
             item.title.toLowerCase().includes('lunch');
    });

    if (lunchSchedule) {
      return `${formatTime(lunchSchedule.startTime)} - ${formatTime(lunchSchedule.endTime)}`;
    }

    // Default lunch time if no specific lunch schedule found
    return `${TIME_CONSTANTS.LUNCH_START_TIME} - ${TIME_CONSTANTS.LUNCH_END_TIME}`;
  }, [scheduleItems, currentDate]);

  // Helper function to get schedule for a specific time range
  const getScheduleForTimeRange = useCallback((staffId: string, startTime: string, endTime: string): string => {
    // Filter today's schedule items first
    const todaySchedules = scheduleItems.filter(item => item.date === formatDate(currentDate));
    
    // Find all schedules for this staff member that overlap with the time range
    const overlappingSchedules = todaySchedules.filter(item => {
      // Exclude lunch breaks from regular time slots
      // A lunch break is a schedule with type 'break' and title containing 'lunch'
      const isLunchBreak = item.type === 'break' && 
                          item.title && 
                          item.title.toLowerCase().includes('lunch');
      
      return item.staffId === staffId && 
             !isLunchBreak &&
             timeOverlaps(item.startTime, item.endTime, startTime, endTime);
    });

    if (overlappingSchedules.length === 0) return '';

    // First check for highest priority (leave/MC)
    const leaveOrMC = overlappingSchedules.find(s => s.type === 'leave' || s.type === 'mc');
    if (leaveOrMC) {
      if (leaveOrMC.type === 'leave') {
        return leaveOrMC.title || 'LEAVE';
      }
      return leaveOrMC.title || 'URGENT LEAVE';
    }

    // Then check for doctor assignments - collect ALL of them
    const doctorSchedules = overlappingSchedules.filter(s => s.doctorId);
    if (doctorSchedules.length > 0) {
      // Get unique doctor names
      const doctorNames = doctorSchedules.map(schedule => {
        const doctor = doctors?.find(d => d.id === schedule.doctorId);
        return doctor ? `Dr ${doctor.short_name || doctor.name}` : '';
      }).filter(name => name); // Remove empty strings
      
      // Remove duplicates and join with comma
      const uniqueDoctorNames = [...new Set(doctorNames)];
      return uniqueDoctorNames.join(', ');
    }

    // Check for admin tasks
    const adminSchedule = overlappingSchedules.find(s => s.type === 'admin');
    if (adminSchedule) {
      return adminSchedule.title || 'Admin Task';
    }

    // For other tasks
    const otherSchedule = overlappingSchedules[0];
    return otherSchedule?.title || '';
  }, [scheduleItems, currentDate, doctors]);

  // Helper function to get Overrun schedules (only schedules that start after 12pm)
  const getOverrunSchedule = useCallback((staffId: string): string => {
    // Filter today's schedule items first
    const todaySchedules = scheduleItems.filter(item => item.date === formatDate(currentDate));
    
    // Find schedules for this staff member that start after 12:00 but before 12:50
    const overrunSchedules = todaySchedules.filter(item => {
      // Exclude lunch breaks
      // A lunch break is a schedule with type 'break' and title containing 'lunch'
      const isLunchBreak = item.type === 'break' && 
                          item.title && 
                          item.title.toLowerCase().includes('lunch');
      
      return item.staffId === staffId && 
             !isLunchBreak &&
             isTimeAfter(item.startTime, TIME_CONSTANTS.OVERRUN_START) && 
             isTimeBefore(item.startTime, TIME_CONSTANTS.OVERRUN_END); // Only schedules that START between 12:00-12:50
    });

    if (overrunSchedules.length === 0) return '';

    // First check for highest priority (leave/MC)
    const leaveOrMC = overrunSchedules.find(s => s.type === 'leave' || s.type === 'mc');
    if (leaveOrMC) {
      if (leaveOrMC.type === 'leave') {
        return leaveOrMC.title || 'LEAVE';
      }
      return leaveOrMC.title || 'URGENT LEAVE';
    }

    // Then check for doctor assignments - collect ALL of them
    const doctorSchedules = overrunSchedules.filter(s => s.doctorId);
    if (doctorSchedules.length > 0) {
      // Get unique doctor names
      const doctorNames = doctorSchedules.map(schedule => {
        const doctor = doctors?.find(d => d.id === schedule.doctorId);
        return doctor ? `Dr ${doctor.short_name || doctor.name}` : '';
      }).filter(name => name); // Remove empty strings
      
      // Remove duplicates and join with comma
      const uniqueDoctorNames = [...new Set(doctorNames)];
      return uniqueDoctorNames.join(', ');
    }

    // Check for admin tasks
    const adminSchedule = overrunSchedules.find(s => s.type === 'admin');
    if (adminSchedule) {
      return adminSchedule.title || 'Admin Task';
    }

    // For other tasks
    const otherSchedule = overrunSchedules[0];
    return otherSchedule?.title || '';
  }, [scheduleItems, currentDate, doctors]);

  // Helper function to get OT schedules (only schedules that start after 6pm, or after 1:15pm on Saturday)
  const getOTSchedule = useCallback((staffId: string): string => {
    // Filter today's schedule items first
    const todaySchedules = scheduleItems.filter(item => item.date === formatDate(currentDate));
    
    // Determine OT start time based on day of week
    const otStartTime = isSaturday(currentDate) ? TIME_CONSTANTS.SATURDAY_OT_START : TIME_CONSTANTS.OVERTIME_START;
    
    // Find schedules for this staff member that start after OT threshold
    const otSchedules = todaySchedules.filter(item => {
      // Exclude lunch breaks
      // A lunch break is a schedule with type 'break' and title containing 'lunch'
      const isLunchBreak = item.type === 'break' && 
                          item.title && 
                          item.title.toLowerCase().includes('lunch');
      
      return item.staffId === staffId && 
             !isLunchBreak &&
             isTimeOnOrAfter(item.startTime, otStartTime); // Only schedules that START after OT threshold
    });

    if (otSchedules.length === 0) return '';

    // First check for highest priority (leave/MC)
    const leaveOrMC = otSchedules.find(s => s.type === 'leave' || s.type === 'mc');
    if (leaveOrMC) {
      if (leaveOrMC.type === 'leave') {
        return leaveOrMC.title || 'LEAVE';
      }
      return leaveOrMC.title || 'URGENT LEAVE';
    }

    // Then check for doctor assignments - collect ALL of them
    const doctorSchedules = otSchedules.filter(s => s.doctorId);
    if (doctorSchedules.length > 0) {
      // Get unique doctor names
      const doctorNames = doctorSchedules.map(schedule => {
        const doctor = doctors?.find(d => d.id === schedule.doctorId);
        return doctor ? `Dr ${doctor.short_name || doctor.name}` : '';
      }).filter(name => name); // Remove empty strings
      
      // Remove duplicates and join with comma
      const uniqueDoctorNames = [...new Set(doctorNames)];
      return uniqueDoctorNames.join(', ');
    }

    // Check for admin tasks
    const adminSchedule = otSchedules.find(s => s.type === 'admin');
    if (adminSchedule) {
      return adminSchedule.title || 'Admin Task';
    }

    // For other tasks
    const otherSchedule = otSchedules[0];
    return otherSchedule?.title || '';
  }, [scheduleItems, currentDate, doctors]);

  // Prepare staff schedules (only staff, not doctors) - memoized for performance
  const staffSchedules: StaffSchedule[] = useMemo(() => {
    return staff.map(person => {
      const isSat = isSaturday(currentDate);
      
      // For Saturday, extend AM2 until 1:00 PM
      const am2EndTime = isSat ? TIME_CONSTANTS.SATURDAY_AM2_END : TIME_CONSTANTS.AM2_END;
      
      const am1 = getScheduleForTimeRange(person.id, TIME_CONSTANTS.AM1_START, TIME_CONSTANTS.AM1_END);
      const am2 = getScheduleForTimeRange(person.id, TIME_CONSTANTS.AM2_START, am2EndTime);
      const overrun = isSat ? '' : getOverrunSchedule(person.id); // No overrun on Saturday
      const pm1 = isSat ? '' : getScheduleForTimeRange(person.id, TIME_CONSTANTS.PM1_START, TIME_CONSTANTS.PM1_END);
      const pm2 = isSat ? '' : getScheduleForTimeRange(person.id, TIME_CONSTANTS.PM2_START, TIME_CONSTANTS.PM2_END);
      const ot = getOTSchedule(person.id); // OT calculation handles Saturday differently

      // Check if staff is on leave for the entire day by checking the actual schedule types
      const todaySchedules = scheduleItems.filter(item => 
        item.date === formatDate(currentDate) && 
        item.staffId === person.id
      );
      
      // Check if staff is on leave for the entire day
      // This is true if they have at least one leave/mc schedule that covers the main working hours
      const hasLeaveSchedule = todaySchedules.some(item => 
        (item.type === 'leave' || item.type === 'mc') &&
        isTimeOnOrBefore(item.startTime, '09:00') &&
        isTimeOnOrAfter(item.endTime, '18:00')
      );
      
      // Or if all their assigned time slots show leave
      const hasLeaveInSlots = [am1, am2, pm1, pm2].some(slot => 
        slot && (slot.includes('LEAVE') || slot === 'MC')
      );
      const allSlotsAreLeaveOrEmpty = [am1, am2, pm1, pm2].every(slot => 
        !slot || slot === '' || slot.includes('LEAVE') || slot === 'MC'
      );
      const allSlotsAreLeave = hasLeaveInSlots && allSlotsAreLeaveOrEmpty;
      
      const isOnLeaveAll = hasLeaveSchedule || allSlotsAreLeave;

      return {
        staffId: person.id,
        name: person.short_name || person.name,
        priority: person.priority || 999, // Add priority field, default to 999 if not set
        location: currentClinicName || UI_CONSTANTS.DEFAULT_LOCATION, // Use current clinic name or default
        am1,
        am2,
        overrun,
        lunch: getLunchTime(person.id, isOnLeaveAll),
        pm1,
        pm2,
        ot,
        roomAM: getRoomForTimePeriod(person.id, TIME_CONSTANTS.AM1_START, TIME_CONSTANTS.AM2_END), // AM period
        roomPM: getRoomForTimePeriod(person.id, TIME_CONSTANTS.PM1_START, TIME_CONSTANTS.PM2_END), // PM period
        isOnLeaveAll,
      };
    }).filter(schedule => {
      // Hide staff with no schedules (all time slots are empty)
      return schedule.am1 || schedule.am2 || schedule.overrun || schedule.pm1 || schedule.pm2 || schedule.ot;
    }).sort((a, b) => {
      // Sort by priority (lowest number first)
      return a.priority - b.priority;
    });
  }, [staff, scheduleItems, currentDate, getScheduleForTimeRange, getOverrunSchedule, getOTSchedule, getLunchTime, getRoomForTimePeriod, currentClinicName]);

  const morningRoomAvailability = getRoomAvailabilityDetails('morning');
  const afternoonRoomAvailability = getRoomAvailabilityDetails('afternoon');
  const isSat = isSaturday(currentDate);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-[90vw] p-3 sm:p-4">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base">
            Daily Manpower Schedule - {currentDate.toLocaleDateString('en-SG', {
              weekday: 'long',
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </DialogTitle>
        </DialogHeader>

        {/* Available Rooms Section */}
        <div className="mb-4 p-3 bg-muted/50 rounded-lg">
          <div className="text-sm font-medium mb-3">Room Availability</div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Morning Availability - Left Side */}
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Morning ({isSat ? '9:00AM - 1:00PM' : '9:00AM - 12:00PM'})
              </div>
              <div className="space-y-1">
                {morningRoomAvailability.length > 0 ? (
                  morningRoomAvailability.map((roomInfo, index) => (
                    <div key={index} className="text-sm">
                      <span className={roomInfo.hasAvailability ? "text-green-600" : "text-red-600"}>
                        {roomInfo.availabilityString}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500">No rooms configured</div>
                )}
              </div>
            </div>
            
            {/* Afternoon Availability - Right Side */}
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {isSat ? 'Afternoon (Not Available on Saturday)' : 'Afternoon (1:00PM - 6:00PM)'}
              </div>
              <div className="space-y-1">
                {isSat ? (
                  <div className="text-sm text-gray-500">No PM sessions on Saturday</div>
                ) : afternoonRoomAvailability.length > 0 ? (
                  afternoonRoomAvailability.map((roomInfo, index) => (
                    <div key={index} className="text-sm">
                      <span className={roomInfo.hasAvailability ? "text-green-600" : "text-red-600"}>
                        {roomInfo.availabilityString}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500">No rooms configured</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-lg">
          <Table style={{ borderCollapse: 'collapse', borderSpacing: 0 }}>
            <TableHeader>
              <TableRow className="border-b hover:bg-transparent">
                <TableHead className="h-8 text-xs font-medium">Staff</TableHead>
                <TableHead className="h-8 text-xs font-medium">Location</TableHead>
                <TableHead className="h-8 text-xs font-medium">Room</TableHead>
                <TableHead className="h-8 text-xs font-medium">AM</TableHead>
                <TableHead className="h-8 text-xs font-medium">{isSat ? 'AM (till 1PM)' : 'AM'}</TableHead>
                <TableHead className="h-8 text-xs font-medium">{isSat ? '' : 'Over run'}</TableHead>
                <TableHead className="h-8 text-xs font-medium">{isSat ? '' : 'Lunch'}</TableHead>
                <TableHead className="h-8 text-xs font-medium">{isSat ? '' : 'Room'}</TableHead>
                <TableHead className="h-8 text-xs font-medium">{isSat ? '' : 'PM'}</TableHead>
                <TableHead className="h-8 text-xs font-medium">{isSat ? '' : 'PM'}</TableHead>
                <TableHead className="h-8 text-xs font-medium">{isSat ? 'OT (1:15PM+)' : 'OT'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staffSchedules.map((schedule) => {
                // Check the original schedule items to see if any are MC
                const todaySchedules = scheduleItems.filter(item => 
                  item.date === formatDate(currentDate) && 
                  item.staffId === schedule.staffId
                );
                
                // Only colour the WHOLE row as MC/leave when the leave covers the full
                // working day (09:00-18:00). A partial-day leave (e.g. an afternoon-only
                // AL) must NOT paint the morning cells, otherwise a staff member who
                // works the morning wrongly shows as on leave all day.
                const hasMCInRow = todaySchedules.some(item =>
                  item.type === 'mc' &&  // mc is displayed as "Urgent Leave"
                  isTimeOnOrBefore(item.startTime, '09:00') &&
                  isTimeOnOrAfter(item.endTime, '18:00')
                );

                // Check if this staff is on full-day leave
                const hasLeaveInRow = todaySchedules.some(item =>
                  item.type === 'leave' &&
                  isTimeOnOrBefore(item.startTime, '09:00') &&
                  isTimeOnOrAfter(item.endTime, '18:00')
                );

                // Per-half leave/MC so a partial-day leave colours only its own half
                // (e.g. afternoon AL reds the PM cells, morning stays a normal shift).
                const amHasMC = todaySchedules.some(item =>
                  item.type === 'mc' &&
                  timeOverlaps(item.startTime, item.endTime, TIME_CONSTANTS.AM1_START, TIME_CONSTANTS.AM2_END)
                );
                const amHasLeave = todaySchedules.some(item =>
                  item.type === 'leave' &&
                  timeOverlaps(item.startTime, item.endTime, TIME_CONSTANTS.AM1_START, TIME_CONSTANTS.AM2_END)
                );
                const pmHasMC = todaySchedules.some(item =>
                  item.type === 'mc' &&
                  timeOverlaps(item.startTime, item.endTime, TIME_CONSTANTS.PM1_START, TIME_CONSTANTS.PM2_END)
                );
                const pmHasLeave = todaySchedules.some(item =>
                  item.type === 'leave' &&
                  timeOverlaps(item.startTime, item.endTime, TIME_CONSTANTS.PM1_START, TIME_CONSTANTS.PM2_END)
                );
                const amMC = hasMCInRow || amHasMC;
                const amLeave = hasLeaveInRow || amHasLeave;
                const pmMC = hasMCInRow || pmHasMC;
                const pmLeave = hasLeaveInRow || pmHasLeave;

                return (
                  <TableRow 
                    key={schedule.staffId} 
                    className="border-b hover:bg-muted/50"
                  >
                    <TableCell className={getCellColorClass('', 'staff', hasMCInRow, hasLeaveInRow)}>{schedule.name}</TableCell>
                    <TableCell className={getCellColorClass('', 'location', hasMCInRow, hasLeaveInRow)}>{schedule.location}</TableCell>
                    <TableCell className={getCellColorClass(schedule.roomAM, 'roomAM', amMC, amLeave)}>{schedule.roomAM}</TableCell>
                    <TableCell className={getCellColorClass(schedule.am1, 'am1', amMC, amLeave)}>{schedule.am1}</TableCell>
                    <TableCell className={getCellColorClass(schedule.am2, 'am2', amMC, amLeave)}>{schedule.am2}</TableCell>
                    {isSat ? (
                      <>
                        <TableCell className={getCellColorClass('', 'overrun', hasMCInRow, hasLeaveInRow, true)} />
                        <TableCell className={getCellColorClass('', 'lunch', hasMCInRow, hasLeaveInRow, true)} />
                        <TableCell className={getCellColorClass('', 'roomPM', hasMCInRow, hasLeaveInRow, true)} />
                        <TableCell className={getCellColorClass('', 'pm1', hasMCInRow, hasLeaveInRow, true)} />
                        <TableCell className={getCellColorClass('', 'pm2', hasMCInRow, hasLeaveInRow, true)} />
                      </>
                    ) : (
                      <>
                        <TableCell className={getCellColorClass(schedule.overrun, 'overrun', hasMCInRow, hasLeaveInRow)}>{schedule.overrun}</TableCell>
                        <TableCell className={getCellColorClass(schedule.lunch, 'lunch', hasMCInRow, hasLeaveInRow)}>{schedule.lunch}</TableCell>
                        <TableCell className={getCellColorClass(schedule.roomPM, 'roomPM', pmMC, pmLeave)}>{schedule.roomPM}</TableCell>
                        <TableCell className={getCellColorClass(schedule.pm1, 'pm1', pmMC, pmLeave)}>{schedule.pm1}</TableCell>
                        <TableCell className={getCellColorClass(schedule.pm2, 'pm2', pmMC, pmLeave)}>{schedule.pm2}</TableCell>
                      </>
                    )}
                    <TableCell className={getCellColorClass(schedule.ot, 'ot', hasMCInRow, hasLeaveInRow)}>{schedule.ot}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManpowerScheduleModal; 
